import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, TextField, Switch,
  FormControlLabel, CircularProgress, Alert, Divider,
} from '@mui/material';
import { tenantAPI } from '../../services/api';

// Per-tenant accounting: everything derives automatically.
// - Database: accounting_<tenant_code>
// - Bank / revenue accounts: resolved by code (1002 / 4000) at Finish time
// - Port: part of the Ledger API URL (e.g. http://localhost:3101)
// The operator only ever types ONE url, then Provision -> Finish.
const TenantAccountingSection = ({ tenantId, getAuthConfig }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ enabled: false, base_url: '', revenue_account_map: '{}' });
  const [defaults, setDefaults] = useState({ base_url: '' });
  const [status, setStatus] = useState({ database_exists: false, api_reachable: false });

  const auth = () => (typeof getAuthConfig === 'function' ? getAuthConfig() : getAuthConfig);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await tenantAPI.getAccounting(tenantId, auth());
      const { enabled, config, defaults: dflts, status: st } = res.data;
      setForm({
        enabled: !!enabled,
        base_url: config.base_url || '',
        revenue_account_map: JSON.stringify(config.revenue_account_map || {}, null, 2),
      });
      setDefaults({ base_url: (dflts && dflts.base_url) || '' });
      setStatus(st || {});
    } catch (e) {
      const msg = e?.response?.status === 401
        ? 'Unauthenticated - your admin session expired. Log in again as platform admin.'
        : (e?.response?.data?.message || 'Failed to load accounting settings.');
      setError(msg);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    setResult(null);
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      let revenueAccountMap;
      try {
        revenueAccountMap = JSON.parse(form.revenue_account_map || '{}');
      } catch {
        throw new Error('Fee revenue account mapping must be valid JSON.');
      }
      if (!revenueAccountMap || Array.isArray(revenueAccountMap) || typeof revenueAccountMap !== 'object') {
        throw new Error('Fee revenue account mapping must be a JSON object.');
      }
      const normalizedRevenueAccountMap = Object.create(null);
      Object.entries(revenueAccountMap).forEach(([rawKey, rawAccountId]) => {
        const key = rawKey.trim();
        if (!key) throw new Error('Fee revenue account mapping keys cannot be blank.');
        const numericKey = /^\d+$/.test(key);
        const normalizedKey = numericKey ? key.replace(/^0+(?=\d)/, '') : key.toLowerCase();
        if (/^\d+$/.test(key) && Number(normalizedKey) < 1) {
          throw new Error(`Fee mapping key "${rawKey}" must be a positive fee type ID or a fee type name.`);
        }
        const accountId = Number(rawAccountId);
        if (!Number.isSafeInteger(accountId) || accountId < 1) {
          throw new Error(`Fee mapping for "${rawKey}" must use a positive whole-number ledger account ID.`);
        }
        if (Object.prototype.hasOwnProperty.call(normalizedRevenueAccountMap, normalizedKey)) {
          throw new Error(`Fee mapping contains duplicate keys after normalization: "${normalizedKey}".`);
        }
        normalizedRevenueAccountMap[normalizedKey] = accountId;
      });
      await tenantAPI.saveAccounting(tenantId, {
        enabled: form.enabled,
        base_url: form.base_url,
        revenue_account_map: normalizedRevenueAccountMap,
      }, auth());
      setResult({ ok: true, message: 'Saved.' });
      load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const runStep = async (kind) => {
    setRunning(kind);
    setError(null);
    setResult(null);
    try {
      const res = kind === 'provision'
        ? await tenantAPI.provisionAccounting(tenantId, auth())
        : await tenantAPI.finishAccounting(tenantId, auth());
      setResult({ ok: true, message: res.data.message, output: res.data.output });
      load();
    } catch (e) {
      const msg = e?.response?.data?.message || `${kind} failed.`;
      const out = e?.response?.data?.output;
      setError(msg);
      if (out) setResult({ ok: false, message: msg, output: out });
    } finally {
      setRunning(null);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>;
  }

  return (
    <Box mt={3}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>Accounting</Typography>
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Chip label={form.enabled ? 'Enabled' : 'Disabled'} color={form.enabled ? 'success' : 'default'} size="small" />
        <Chip label={status.database_exists ? 'Ledger DB exists' : 'No ledger DB'} color={status.database_exists ? 'success' : 'warning'} size="small" variant="outlined" />
        <Chip label={status.api_reachable ? 'API reachable' : 'API unreachable'} color={status.api_reachable ? 'success' : 'warning'} size="small" variant="outlined" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {result?.ok && <Alert severity="success" sx={{ mb: 2 }}>{result.message}</Alert>}
      {result?.output && (
        <Box component="pre" sx={{ fontSize: 11, bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1, overflowX: 'auto', maxHeight: 220, overflowY: 'auto', mb: 2 }}>
          {result.output}
        </Box>
      )}

      <FormControlLabel
        control={<Switch checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />}
        label="Accounting enabled for this school"
        sx={{ mb: 1 }}
      />
      <TextField
        label="Ledger API URL (optional)"
        value={form.base_url}
        onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
        placeholder={defaults.base_url || 'http://localhost:3100'}
        helperText="Blank = use the system default from .env (ACCOUNTING_BASE_URL). Only set this if the school runs its own ledger instance."
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Fee type → revenue account map (JSON)"
        value={form.revenue_account_map}
        onChange={(e) => setForm((f) => ({ ...f, revenue_account_map: e.target.value }))}
        helperText={'Use positive fee type IDs or fee type names as keys, for example {"1": 4001, "transport": 4002}. Names are trimmed and lower-cased on save; values must be positive whole-number ledger account IDs. The bridge default revenue account is fallback.'}
        size="small"
        fullWidth
        multiline
        minRows={3}
        sx={{ mb: 2, fontFamily: 'monospace' }}
      />

      <Box display="flex" gap={1} flexWrap="wrap">
        <Button variant="contained" size="small" disabled={saving} onClick={save}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button variant="outlined" size="small" disabled={running !== null} onClick={() => runStep('provision')}>
          {running === 'provision' ? 'Provisioning...' : '1. Provision ledger DB'}
        </Button>
        <Button variant="outlined" size="small" disabled={running !== null} onClick={() => runStep('finish')}>
          {running === 'finish' ? 'Finishing...' : '2. Seed + create accounts'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        Provision builds the database. Then start that school's API instance at the URL above, then Finish creates its two service accounts and switches it on.
      </Typography>
    </Box>
  );
};

export default TenantAccountingSection;
